import api from '@/api/methods/index';
import store from '@/vuex/index';

const DEFAULT_ALLOCATE_TYPE_LIST = [
    {id: 1, name: '工程款', status: 1},
    {id: 2, name: '质保金', status: 1},
    {id: 3, name: '二类费用', status: 1},
    {id: 4, name: '其他', status: 1},
];

let allocateTypePromise = null;

// 读取 store 中已缓存的拨付类型列表，作为当前页面的首选数据源。
function getStoreAllocateTypeList() {
    return store.state.user.allocate_type_list || [];
}

// 基于缓存列表或默认列表返回当前可用的拨付类型集合。
function getCurrentAllocateTypeList(list = getStoreAllocateTypeList()) {
    return list.length ? list : DEFAULT_ALLOCATE_TYPE_LIST;
}

// 兼容接口原始列表和下拉 option 列表两种结构读取类型 id。
function getAllocateTypeValue(item = {}) {
    return item.id !== undefined ? item.id : item.value;
}

// 兼容接口原始列表和下拉 option 列表两种结构读取类型名称。
function getAllocateTypeLabel(item = {}) {
    return item.name !== undefined ? item.name : item.label;
}

// 系统默认拨付类型不允许删除，优先按 id 判断，同时用名称兜底兼容下拉 option 结构。
function isDefaultAllocateType(item = {}) {
    let currentValue = getAllocateTypeValue(item);
    let currentLabel = String(getAllocateTypeLabel(item)).trim();
    return DEFAULT_ALLOCATE_TYPE_LIST.some(defaultItem => {
        return String(defaultItem.id) === String(currentValue) || defaultItem.name === currentLabel;
    });
}

// 按类型 id 查找拨付类型对象，兼容字符串/数字两种比较方式。
export function getAllocateTypeItemById(value, list = getStoreAllocateTypeList()) {
    let targetList = getCurrentAllocateTypeList(list);
    return targetList.find(item => {
        let currentValue = getAllocateTypeValue(item);
        return currentValue === value || String(currentValue) === String(value);
    }) || null;
}

// 按类型名称查找拨付类型对象，避免 allow-create 输入已存在名称时重复创建。
export function getAllocateTypeItemByName(name, list = getStoreAllocateTypeList()) {
    if (name === undefined || name === null || name === '') {
        return null;
    }
    let targetList = getCurrentAllocateTypeList(list);
    let targetName = String(name).trim();
    return targetList.find(item => String(getAllocateTypeLabel(item)).trim() === targetName) || null;
}

// 将拨付类型列表转换成下拉组件可直接使用的 value/label 结构。
export function getAllocateTypeOptions(list = getStoreAllocateTypeList()) {
    // store 里还没有接口数据时，先退回默认类型，避免新增/编辑弹窗无选项可选。
    let targetList = getCurrentAllocateTypeList(list);
    return targetList.map(item => ({
        value: getAllocateTypeValue(item),
        label: getAllocateTypeLabel(item),
        deletable: !isDefaultAllocateType(item),
    }));
}

// 确保拨付类型列表已加载到 store，支持强制刷新，并复用进行中的请求避免重复调接口。
export function ensureAllocateTypeList(force = false) {
    let cacheList = getStoreAllocateTypeList();
    // 非强制刷新场景优先复用已缓存结果，减少页面重复进入时的请求次数。
    if (!force && cacheList.length) {
        return Promise.resolve(cacheList);
    }
    // 同一时间多个组件都需要拨付类型时，直接复用当前 promise，避免并发重复请求。
    if (!force && allocateTypePromise) {
        return allocateTypePromise;
    }
    // 缓存正在进行中的请求，避免同页多个组件重复拉取拨付类型。
    allocateTypePromise = api.project.allocateTypeList({}).then(res => {
        let list = res?.data?.state == 1 ? (res.data.lists || []) : [];
        store.commit('user/SET_ALLOCATE_TYPE_LIST', list);
        return list;
    }).catch(() => {
        return cacheList;
    }).finally(() => {
        allocateTypePromise = null;
    });
    return allocateTypePromise;
}

// 处理 allow-create 产生的新拨付类型值：已存在则直接复用，不存在则先保存后刷新列表并回填 id。
export async function ensureAllocateTypeId(value) {
    if (value === undefined || value === null || value === '') {
        return value;
    }
    let list = await ensureAllocateTypeList();
    let currentById = getAllocateTypeItemById(value, list);
    if (currentById) {
        return currentById.id;
    }
    let currentByName = getAllocateTypeItemByName(value, list);
    if (currentByName) {
        return currentByName.id;
    }
    let name = String(value).trim();
    if (!name) {
        return '';
    }
    let saveRes = await api.project.allocateTypeSave({name});
    if (!(saveRes && saveRes.data && saveRes.data.state == 1)) {
        throw new Error('allocate type save failed');
    }
    let latestList = await ensureAllocateTypeList(true);
    let savedItem = getAllocateTypeItemByName(name, latestList);
    if (!savedItem) {
        throw new Error('allocate type missing after save');
    }
    return savedItem.id;
}

// 删除拨付类型后强制刷新列表，保证下拉选项和 store 缓存同步更新。
export async function deleteAllocateType(id) {
    if (id === undefined || id === null || id === '') {
        throw new Error('allocate type id missing');
    }
    let deleteRes = await api.project.allocateTypeDelete({id});
    if (!(deleteRes && deleteRes.data && deleteRes.data.state == 1)) {
        throw new Error('allocate type delete failed');
    }
    return ensureAllocateTypeList(true);
}
